import { Injectable, computed, signal } from '@angular/core';
import { PerfilUsuario, SessaoUsuario } from '../models/sessao-usuario.model';

const CHAVE_SESSAO = 'sisgestao.sessao';

@Injectable({ providedIn: 'root' })
export class SessaoStore {
  private readonly sessao = signal<SessaoUsuario | null>(this.restaurar());
  private versao = 0;

  readonly perfil = computed<PerfilUsuario | null>(() => {
    const atual = this.sessao();
    return atual ? { usuarioId: atual.usuarioId, login: atual.login, nome: atual.nome, nivel: atual.nivel } : null;
  });
  readonly nomeExibicao = computed(() => this.sessao()?.nome?.trim() || this.sessao()?.login.trim() || '');
  readonly token = computed(() => this.sessao()?.token ?? null);
  readonly expiraEm = computed(() => this.sessao()?.expiraEm ?? null);
  readonly nivel = computed(() => this.sessao()?.nivel ?? null);
  readonly isAutenticado = computed(() => this.sessao() !== null);

  get versaoSessao(): number { return this.versao; }

  abrir(sessao: SessaoUsuario): void {
    this.versao++;
    this.sessao.set({ ...sessao });
    this.persistir();
  }

  /** Validade verificada no próximo acesso; o instante exato do vencimento ainda é aceito. */
  verificarValidade(): boolean {
    const atual = this.sessao();
    if (atual && Date.now() > Date.parse(atual.expiraEm)) this.limpar();
    return this.isAutenticado();
  }

  atualizarToken(token: string, versao: number): void {
    const atual = this.sessao();
    if (!atual || versao !== this.versao) return;
    const vencimento = this.lerVencimento(token);
    // Respostas concorrentes podem chegar fora de ordem; não encurtar a sessão nem reabri-la após sair.
    if (vencimento === null || vencimento < Date.parse(atual.expiraEm)) return;
    this.sessao.set({ ...atual, token, expiraEm: new Date(vencimento).toISOString() });
    this.persistir();
  }

  atualizarNome(nome: string | null): void {
    const atual = this.sessao();
    if (!atual) return;
    this.sessao.set({ ...atual, nome });
    this.persistir();
  }

  limpar(): void {
    this.versao++;
    this.sessao.set(null);
    try { sessionStorage.removeItem(CHAVE_SESSAO); } catch { /* Storage pode estar indisponível no navegador. */ }
  }

  private lerVencimento(token: string): number | null {
    try {
      const parte = token.split('.')[1];
      if (!parte) return null;
      const base64 = parte.replace(/-/g, '+').replace(/_/g, '/');
      const payload: unknown = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
      if (typeof payload !== 'object' || payload === null || !('exp' in payload)
        || typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) return null;
      const instante = payload.exp * 1000;
      return Number.isFinite(instante) && Math.abs(instante) <= 8640000000000000 ? instante : null;
    } catch { return null; }
  }

  private restaurar(): SessaoUsuario | null {
    try {
      const texto = sessionStorage.getItem(CHAVE_SESSAO);
      if (!texto) return null;
      const valor: unknown = JSON.parse(texto);
      if (this.ehSessao(valor) && Date.now() <= Date.parse(valor.expiraEm)) return valor;
    } catch { /* Estado ausente ou inválido não concede acesso. */ }
    try { sessionStorage.removeItem(CHAVE_SESSAO); } catch { /* Sem persistência disponível. */ }
    return null;
  }

  private ehSessao(valor: unknown): valor is SessaoUsuario {
    if (typeof valor !== 'object' || valor === null) return false;
    return 'usuarioId' in valor && typeof valor.usuarioId === 'number' && Number.isInteger(valor.usuarioId)
      && 'login' in valor && typeof valor.login === 'string'
      && (!('nome' in valor) || valor.nome === undefined || valor.nome === null || typeof valor.nome === 'string')
      && 'nivel' in valor && (valor.nivel === null || (typeof valor.nivel === 'number' && Number.isInteger(valor.nivel)))
      && 'token' in valor && typeof valor.token === 'string' && valor.token.length > 0
      && 'expiraEm' in valor && typeof valor.expiraEm === 'string' && Number.isFinite(Date.parse(valor.expiraEm));
  }

  private persistir(): void {
    try { sessionStorage.setItem(CHAVE_SESSAO, JSON.stringify(this.sessao())); } catch { /* Mantém a sessão apenas em memória. */ }
  }
}
